import React from 'react';
import { motion } from 'framer-motion';
import { Play, Quote } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ConfettiAnimation } from '../components/ConfettiAnimation';
interface WelcomeScreenProps {
  onNext: () => void;
}
export function WelcomeScreen({
  onNext
}: WelcomeScreenProps) {
  return <div className="relative min-h-screen flex flex-col items-center justify-center p-6 overflow-hidden bg-[var(--bg)] transition-colors duration-300" data-id="element-196">
      <ConfettiAnimation data-id="element-197" />

      <div className="max-w-4xl w-full z-10 flex flex-col items-center text-center" data-id="element-198">
        {/* Header Text */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="mb-8" data-id="element-199">
          <img src="https://cdn.magicpatterns.com/uploads/vATSLiZa5nUGunFUCfrpXw/welcome.svg" alt="Welcome to ClickFunnels" className="h-10 md:h-14 mb-6" data-id="element-200" />
          <p className="text-xl md:text-2xl text-[var(--text-secondary)] font-light transition-colors duration-300" data-id="element-201">
            You're officially a funnel builder.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="mb-10" data-id="element-202">
          <Button size="lg" onClick={onNext} className="text-base px-8 py-4 h-auto font-bold" data-id="element-203">
            Let's build your first funnel
          </Button>
        </motion.div>

        {/* Video Placeholder */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="w-full max-w-2xl aspect-video bg-[#1a1f36] rounded-2xl border border-[#e5e2dc] shadow-2xl shadow-[var(--shadow-lg)] relative group cursor-pointer overflow-hidden mb-12" data-id="element-204">
          {/* Thumbnail/Placeholder Content */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f36] to-[#0f172a] flex items-center justify-center" data-id="element-205">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20 mix-blend-overlay" data-id="element-206" />

            <div className="w-20 h-20 rounded-full bg-[#ffc978] flex items-center justify-center shadow-lg shadow-[#ffc978]/30 group-hover:scale-110 transition-transform duration-300 z-10" data-id="element-207">
              <Play fill="#1a1f36" className="text-[#1a1f36] ml-1" size={32} data-id="element-208" />
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0f172a] to-transparent" data-id="element-209">
            <p className="text-white font-medium" data-id="element-210">
              A message from Todd Dickerson
            </p>
            <p className="text-[#9ca3af] text-sm" data-id="element-211">Co-Founder, ClickFunnels</p>
          </div>
        </motion.div>

        {/* Social Proof */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.8
      }} className="flex flex-col items-center w-full" data-id="element-212">
          <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-8 transition-colors duration-300" data-id="element-213">
            See what a few of our 100,000+ members are saying
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl" data-id="element-214">
            {[{
            quote: 'ClickFunnels replaced 8 different tools I was paying for. Everything I need to run my business is in one place now.',
            name: 'Trey Lewellen',
            role: 'E-commerce Entrepreneur'
          }, {
            quote: 'I went from zero to a 7-figure business using ClickFunnels. The funnels just work — I can focus on my message instead of tech.',
            name: 'Julie Stoian',
            role: 'Digital Marketing Expert'
          }, {
            quote: 'Within 30 days of launching my first funnel, I had more leads than the previous 6 months combined. Game changer.',
            name: 'Stephen Larsen',
            role: 'Funnel Builder & Speaker'
          }].map((testimonial, idx) => <motion.div key={testimonial.name} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8 + idx * 0.15,
            duration: 0.5
          }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 text-left" data-id="element-215">
                <Quote size={16} className="text-[var(--text-muted)] mb-3 opacity-40" data-id="element-216" />
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4" data-id="element-217">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-218">
                  <p className="text-sm font-semibold text-[var(--text-primary)]" data-id="element-219">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]" data-id="element-220">
                    {testimonial.role}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}